import {
  useRef,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  ActivityIndicator,
  Alert,
  AppState,
  Pressable,
  StyleSheet,
  Text,
  View,
  ScrollView,
  Platform,
  useWindowDimensions,
  type StyleProp,
  type TextStyle,
} from 'react-native';
import { Image } from 'expo-image';
import { observer } from 'mobx-react';
import { router, useFocusEffect } from 'expo-router';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useScale } from '@/hooks/useScale';
import { useStores } from '@/stores/StoreProvider';
import { CreateWorkoutPayload } from '@/stores/workoutPlayer/types';
import { deriveModeFromApiWorkout } from '@/stores/workoutSession/WorkoutSessionStore';
import { workoutAssets } from '@/constants/workoutAssets';
import { workoutTranslations } from '@/constants/workoutTranslations';
import { colors } from '@/constants/colors';
import TopbarLogo from '@/components/TopbarLogo';
import DefaultBackground from '@/components/DefaultBackground';
import ResumeActionsMenu from '@/components/ResumeActionsMenu';
import { FocusGuide } from '@/components/FocusGuide';
import { getWeekDay, getWeekNumber, getWeekYear } from '@/services/datetime';
import {
  GUIDED_WORKOUT_TEMPLATE_TYPES,
  YOGA_TEMPLATE_TYPES,
} from '@/constants/workoutTemplateTypes';

// Every template type the home screen can display. Used to attribute hidden
// workouts to the specific modification(s) responsible for blocking them.
const ALL_WORKOUT_TEMPLATE_TYPES = [
  ...GUIDED_WORKOUT_TEMPLATE_TYPES,
  ...YOGA_TEMPLATE_TYPES,
];

const ENABLE_CARD_WORKOUT = false;

const SCROLL_AMOUNT = 600;
const isWeb = Platform.OS === 'web';

function getWorkoutAssets(templateType: string) {
  return (
    workoutAssets[templateType as keyof typeof workoutAssets] ??
    workoutAssets['DEFAULT']
  );
}

// Renders a comma/"and"-joined list of names with each name in bold, while the
// separators stay regular weight: ["A"] -> **A**, ["A","B"] -> **A** and **B**,
// ["A","B","C"] -> **A**, **B** and **C**. Today users only have one modification,
// but the data model allows several, so we handle plural now to be future-proof.
function renderNameList(
  names: string[],
  nameStyle: StyleProp<TextStyle>,
): ReactNode[] {
  return names.flatMap((name, index) => {
    const separator =
      index === 0 ? '' : index === names.length - 1 ? ' and ' : ', ';
    return [
      separator,
      <Text key={name} style={nameStyle}>
        {name}
      </Text>,
    ];
  });
}

function getWorkoutTranslation(templateType: string) {
  return (
    workoutTranslations[templateType as keyof typeof workoutTranslations] ??
    workoutTranslations['FALLBACK']
  );
}

function makeGuidedPayload(templateType: string): CreateWorkoutPayload {
  return {
    workout_id: null,
    fitness_level: 'BEGINNER',
    workout_template_type: templateType,
    focus_areas: null,
    year: getWeekYear(),
    week: getWeekNumber(),
    day: getWeekDay(),
    useMlWorkout: false,
    path_id: null,
    order: null,
  };
}

function makeCardPayload(): CreateWorkoutPayload {
  return {
    workout_id: null,
    fitness_level: 'BEGINNER',
    workout_template_type: 'MINI',
    focus_areas: null,
    year: getWeekYear(),
    week: getWeekNumber(),
    day: getWeekDay(),
    useMlWorkout: false,
    path_id: null,
    order: null,
  };
}
// ---------------------------------------------------------------------------
// WorkoutCarousel — reusable horizontal row with web scroll buttons
// ---------------------------------------------------------------------------

const HORIZONTAL_INSET = 24;
const CARD_ASPECT_RATIO = Number((310 / 190).toFixed(2));
const MAX_CARD_HEIGHT = 220;
const MIN_CARD_HEIGHT = 120;
// Top bar: 24*2 paddingVertical + 50 TopbarLogo height.
const TOP_BAR_HEIGHT = 98;
// Bottom padding on the main vertical scroll container.
const MAIN_SCROLL_BOTTOM_PADDING = 40;
// Breathing room above/below cards so the focused-state transform (scale 1.05)
// and border can render without being clipped by the horizontal ScrollView.
const CARD_FOCUS_PADDING = 8;
// Space between the row title and the card below it.
const TITLE_MARGIN_BOTTOM = 4;
// Per-row title area: title text (~26) + TITLE_MARGIN_BOTTOM.
const ROW_TITLE_AREA = 26 + TITLE_MARGIN_BOTTOM;
// Gap between carousels (mainScrollContent gap).
const ROW_GAP = 16;
// Bottom hint line: fontSize 14 + marginTop 6 + ~2 leading.
const HINT_HEIGHT_LINE = 22;

// Minimal shape of the native card view we need for imperative TV focus.
// react-native-tvos exposes requestTVFocus() on the host View instance, and
// Pressable forwards its ref to that View.
type CardFocusHandle = { requestTVFocus?: () => void };

type WorkoutCarouselProps = {
  title: string;
  templateTypes: string[];
  disabled: boolean;
  loadingTemplateType: string | null;
  onPress: (templateType: string) => void;
  onCardFocus?: (templateType: string) => void;
  pausedTemplateTypes: Set<string>;
  scale: number;
  cardWidth: number;
  cardHeight: number;
  focusTemplateType?: string | null;
  focusNonce?: number;
};

function WorkoutCarousel({
  title,
  templateTypes,
  disabled,
  loadingTemplateType,
  onPress,
  onCardFocus,
  pausedTemplateTypes,
  scale,
  cardWidth,
  cardHeight,
  focusTemplateType = null,
  focusNonce = 0,
}: WorkoutCarouselProps) {
  const scrollRef = useRef<ScrollView>(null);

  const gap = 14 * scale;

  const offsetRef = useRef(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);

  // Native card views, keyed by template type, for imperative TV focus.
  const cardRefs = useRef<Record<string, CardFocusHandle | null>>({});

  // The card this carousel should focus, or null when the target lives in the
  // other carousel (or there is none).
  const focusTarget =
    focusTemplateType && templateTypes.includes(focusTemplateType)
      ? focusTemplateType
      : null;

  // Imperatively claim TV focus for the target card whenever Home regains focus
  // (focusNonce bumps) or the target changes. requestTVFocus is a direct native
  // command, so unlike hasTVPreferredFocus it reliably wins over the top-bar
  // autoFocus guide on both tvOS and Android — including after a full workout.
  // Deferred a frame so the card is laid out and the screen transition settled.
  useEffect(() => {
    if (isWeb || !focusTarget) return;
    const raf = requestAnimationFrame(() => {
      cardRefs.current[focusTarget]?.requestTVFocus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusNonce, focusTarget]);

  const canScrollLeft = scrollOffset > 0;
  const canScrollRight =
    contentWidth > containerWidth &&
    scrollOffset < contentWidth - containerWidth - 1;
  const showArrows = isWeb && contentWidth > containerWidth;

  const scrollBy = useCallback((delta: number) => {
    const next = Math.max(0, offsetRef.current + delta);
    offsetRef.current = next;
    scrollRef.current?.scrollTo?.({ x: next, animated: true });
  }, []);

  const inset = HORIZONTAL_INSET * scale;

  return (
    <View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: TITLE_MARGIN_BOTTOM * scale,
          paddingHorizontal: inset,
        }}
      >
        <ThemedText
          style={{
            color: '#fff',
            fontSize: 22 * scale,
            fontWeight: '700',
          }}
        >
          {title}
        </ThemedText>

        {showArrows && (
          <View style={{ flexDirection: 'row', gap: 8 * scale }}>
            <Pressable
              disabled={!canScrollLeft}
              onPress={() => scrollBy(-SCROLL_AMOUNT * scale)}
              style={({ hovered }) => ({
                width: 36 * scale,
                height: 36 * scale,
                borderRadius: 18 * scale,
                backgroundColor:
                  hovered && canScrollLeft
                    ? 'rgba(255,255,255,0.25)'
                    : 'rgba(255,255,255,0.12)',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canScrollLeft ? 1 : 0.3,
              })}
            >
              <Ionicons name="chevron-back" size={20 * scale} color="#fff" />
            </Pressable>
            <Pressable
              disabled={!canScrollRight}
              onPress={() => scrollBy(SCROLL_AMOUNT * scale)}
              style={({ hovered }) => ({
                width: 36 * scale,
                height: 36 * scale,
                borderRadius: 18 * scale,
                backgroundColor:
                  hovered && canScrollRight
                    ? 'rgba(255,255,255,0.25)'
                    : 'rgba(255,255,255,0.12)',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canScrollRight ? 1 : 0.3,
              })}
            >
              <Ionicons name="chevron-forward" size={20 * scale} color="#fff" />
            </Pressable>
          </View>
        )}
      </View>

      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap,
          paddingHorizontal: inset,
          paddingVertical: CARD_FOCUS_PADDING * scale,
        }}
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
        onContentSizeChange={(w) => setContentWidth(w)}
        onScroll={(e) => {
          const x = e.nativeEvent.contentOffset.x;
          offsetRef.current = x;
          setScrollOffset(x);
        }}
        scrollEventThrottle={16}
      >
        {templateTypes.map((templateType) => {
          const isCardLoading = loadingTemplateType === templateType;
          const isPaused = pausedTemplateTypes.has(templateType);
          const isFocusTarget = templateType === focusTemplateType;
          return (
            <Pressable
              key={templateType}
              ref={(node) => {
                cardRefs.current[templateType] = node as CardFocusHandle | null;
              }}
              // Drives initial focus on first mount; returning-to-Home focus is
              // handled imperatively via requestTVFocus (see the effect above).
              hasTVPreferredFocus={isFocusTarget}
              // On TV, keep cards focusable while a workout loads. Disabling the
              // focused card drops its focus, and the top-bar autoFocus guide
              // then pulls focus to the Settings icon. Re-presses are guarded in
              // handleCardPress instead. Web keeps the disabled behavior.
              disabled={isWeb && disabled}
              style={({ focused, hovered }) => [
                {
                  width: cardWidth,
                  height: cardHeight,
                  borderRadius: 12 * scale,
                  overflow: 'hidden',
                  borderWidth: 3,
                  borderColor: focused || hovered ? '#fff' : 'transparent',
                  transform: [{ scale: focused ? 1.05 : 1 }],
                },
              ]}
              onFocus={() => onCardFocus?.(templateType)}
              onPress={() => onPress(templateType)}
            >
              <Image
                style={StyleSheet.absoluteFill}
                contentFit="cover"
                contentPosition="top"
                source={{ uri: getWorkoutAssets(templateType).pathInfo }}
              />
              {/* Dark gradient overlay for text readability */}
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.75)']}
                style={[StyleSheet.absoluteFill, { top: '50%' }]}
              />
              <View
                style={{
                  flex: 1,
                  justifyContent: 'flex-end',
                  padding: 12 * scale,
                }}
              >
                <ThemedText
                  style={{
                    color: '#fff',
                    fontSize: 16 * scale,
                    fontWeight: '700',
                  }}
                  numberOfLines={2}
                >
                  {getWorkoutTranslation(templateType).title}
                </ThemedText>
              </View>
              {isPaused && (
                <View
                  style={{
                    position: 'absolute',
                    top: 8 * scale,
                    right: 8 * scale,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6 * scale,
                    paddingVertical: 4 * scale,
                    paddingHorizontal: 10 * scale,
                    borderRadius: 999,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderWidth: 1,
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  }}
                >
                  <Ionicons name="play-circle" size={16 * scale} color="#fff" />
                  <ThemedText
                    style={{
                      color: '#fff',
                      fontSize: 12 * scale,
                      fontWeight: '700',
                      letterSpacing: 0.5,
                    }}
                  >
                    RESUME
                  </ThemedText>
                </View>
              )}
              {isCardLoading && (
                <View
                  style={[
                    StyleSheet.absoluteFill,
                    {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  ]}
                >
                  <ActivityIndicator size="large" color="#fff" />
                </View>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// HomeScreen
// ---------------------------------------------------------------------------

function HomeScreen() {
  const {
    userStore,
    workoutPlayerStore,
    guidedWorkoutPlayerStore,
    workoutSessionStore,
    subscriptionStore,
  } = useStores();

  const { fetchUser } = userStore;
  const { fetchPausedWorkouts } = workoutSessionStore;

  const scale = useScale();
  const styles = useStyles();
  const { height: windowHeight } = useWindowDimensions();

  const { currentUser } = userStore;
  const isTemplateAllowed = (t: string) =>
    currentUser?.isWorkoutTypeAvailableForUser(t) ?? true;
  const visibleGuidedTypes =
    GUIDED_WORKOUT_TEMPLATE_TYPES.filter(isTemplateAllowed);
  const visibleYogaTypes = YOGA_TEMPLATE_TYPES.filter(isTemplateAllowed);

  const pausedTemplateTypes = new Set(workoutSessionStore.pausedTemplateTypes);

  const [resumeMenuTemplateType, setResumeMenuTemplateType] = useState<
    string | null
  >(null);
  const rowCount =
    (visibleGuidedTypes.length > 0 ? 1 : 0) +
    (visibleYogaTypes.length > 0 ? 1 : 0);
  // Only the modifications that actually block a displayed template type. Workouts
  // hidden for other reasons won't appear here, so we never name a phantom cause.
  const modificationNames =
    currentUser?.modificationDisplayNamesHiding(ALL_WORKOUT_TEMPLATE_TYPES) ??
    [];

  // When there are no modifications we only show "HiNote Life" fixed
  // When there are modifications, we show two lines in the bottom
  const hintHeight =
    modificationNames.length > 0 ? HINT_HEIGHT_LINE * 2 : HINT_HEIGHT_LINE;

  // Fit all rows within the viewport so the home screen doesn't scroll vertically.
  // Width is derived from the card aspect ratio.
  const availableForCards =
    windowHeight -
    (TOP_BAR_HEIGHT +
      MAIN_SCROLL_BOTTOM_PADDING +
      hintHeight +
      rowCount * (ROW_TITLE_AREA + CARD_FOCUS_PADDING * 2) +
      Math.max(0, rowCount - 1) * ROW_GAP) *
      scale;
  const computedCardHeight =
    rowCount > 0 ? availableForCards / rowCount : MAX_CARD_HEIGHT * scale;
  const cardHeight = Math.max(
    MIN_CARD_HEIGHT * scale,
    Math.min(MAX_CARD_HEIGHT * scale, computedCardHeight),
  );
  const cardWidth = cardHeight * CARD_ASPECT_RATIO;

  const [loadingTemplateType, setLoadingTemplateType] = useState<string | null>(
    null,
  );

  // The workout card that should hold TV focus. Defaults to the first card;
  // set to the last-focused card so returning to Home reclaims focus there.
  const [focusTemplateType, setFocusTemplateType] = useState<string | null>(
    null,
  );
  // Bumped each time Home regains focus (e.g. returning from a workout) to
  // remount the focus-target card so it reclaims TV focus instead of the
  // top-bar Settings icon. Skipped on the first focus since the initial mount
  // already applies hasTVPreferredFocus.
  const [focusNonce, setFocusNonce] = useState(0);
  const hasFocusedOnceRef = useRef(false);
  // The last workout card that held focus. Home stays mounted underneath the
  // workout/settings flow, so this survives the round trip and lets us restore
  // focus to whichever card the user left from — whether or not they actually
  // started the workout. (workoutSessionStore is reset on workout unmount, so
  // it can't tell us this.)
  const lastFocusedTemplateTypeRef = useRef<string | null>(null);
  const handleCardFocus = useCallback((tt: string) => {
    lastFocusedTemplateTypeRef.current = tt;
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchUser();
      fetchPausedWorkouts();
      if (!isWeb) {
        if (hasFocusedOnceRef.current) {
          if (lastFocusedTemplateTypeRef.current) {
            setFocusTemplateType(lastFocusedTemplateTypeRef.current);
          }
          setFocusNonce((n) => n + 1);
        } else {
          hasFocusedOnceRef.current = true;
        }
      }
    }, [fetchUser, fetchPausedWorkouts]),
  );

  // Fall back to the first visible card when the remembered card is no longer
  // shown (e.g. hidden by a modification change while away from Home).
  const effectiveFocusTemplateType =
    focusTemplateType &&
    (visibleGuidedTypes.includes(focusTemplateType) ||
      visibleYogaTypes.includes(focusTemplateType))
      ? focusTemplateType
      : (visibleGuidedTypes[0] ?? visibleYogaTypes[0] ?? null);

  useEffect(() => {
    if (isWeb) {
      return;
    }
    let previousState = AppState.currentState;
    const sub = AppState.addEventListener('change', (nextState) => {
      if (previousState !== 'active' && nextState === 'active') {
        fetchUser();
        fetchPausedWorkouts();
      }
      previousState = nextState;
    });
    return () => sub.remove();
  }, [fetchUser, fetchPausedWorkouts]);

  const isLoading =
    workoutPlayerStore.isCreatingWorkout ||
    guidedWorkoutPlayerStore.isCreatingWorkout ||
    subscriptionStore.isLoading;
  const error =
    workoutPlayerStore.createError || guidedWorkoutPlayerStore.createError;

  const showUnavailableAlert = () => {
    Alert.alert(
      'Workout not available',
      'This workout is not suitable for your current modification. Update your modification in the mobile app to unlock it.',
    );
  };

  // Mobile parity: when starting a new workout of a given template, skip any
  // previously-paused workout OF THAT SAME template so we don't accumulate
  // abandoned rows. Paused workouts of other templates are left alone.
  const skipSameTemplatePaused = async (templateType: string) => {
    if (workoutSessionStore.hasPausedWorkout(templateType)) {
      await workoutSessionStore.skipAndClear(templateType);
    }
  };

  const handleCreateCard = async () => {
    setLoadingTemplateType('MINI');
    try {
      await Promise.all([subscriptionStore.checkSubscription(), fetchUser()]);
      if (!subscriptionStore.isSubscribed) {
        router.push('/(main)/subscribe');
        return;
      }
      const latestUser = userStore.currentUser;
      if (latestUser && !latestUser.isWorkoutTypeAvailableForUser('MINI')) {
        showUnavailableAlert();
        return;
      }
      await skipSameTemplatePaused('MINI');
      workoutSessionStore.setMode('card');
      workoutSessionStore.setTemplateType('MINI');
      workoutPlayerStore.createAndLoadWorkout(makeCardPayload());
      router.push('/(workout)/pre-workout');
    } catch (error) {
      console.warn('[handleCreateCard] Error creating card workout', error);
    } finally {
      setLoadingTemplateType(null);
    }
  };

  const handleCreateGuided = async (tt: string) => {
    setLoadingTemplateType(tt);
    try {
      await Promise.all([subscriptionStore.checkSubscription(), fetchUser()]);
      if (!subscriptionStore.isSubscribed) {
        router.push('/(main)/subscribe');
        return;
      }
      const latestUser = userStore.currentUser;
      // We already fetch the user on focus and when going from background
      // to foreground. So the "show alert" path is handling the edge case
      // when you update your modification in the mobile app while having
      // the TV app open
      if (latestUser && !latestUser.isWorkoutTypeAvailableForUser(tt)) {
        showUnavailableAlert();
        return;
      }
      await skipSameTemplatePaused(tt);
      workoutSessionStore.setMode('guided');
      workoutSessionStore.setTemplateType(tt);
      guidedWorkoutPlayerStore.createAndLoadWorkout(makeGuidedPayload(tt));
      router.push('/(workout)/pre-workout');
    } catch (error) {
      console.warn('[handleCreateGuided] Error creating guided workout', error);
    } finally {
      setLoadingTemplateType(null);
    }
  };

  const handleCardPress = (templateType: string) => {
    // Ignore re-presses while a workout is already being prepared. This used to
    // be enforced by disabling every card, but that drops TV focus mid-load.
    if (loadingTemplateType) return;
    if (workoutSessionStore.hasPausedWorkout(templateType)) {
      setResumeMenuTemplateType(templateType);
      return;
    }
    handleCreateGuided(templateType);
  };

  const handleResume = async () => {
    const tt = resumeMenuTemplateType;
    setResumeMenuTemplateType(null);
    if (!tt) return;

    const apiWorkout = workoutSessionStore.getPausedWorkoutData(tt);
    if (!apiWorkout) {
      console.warn('[handleResume] No cached workout data for', tt);
      return;
    }

    setLoadingTemplateType(tt);
    try {
      await Promise.all([subscriptionStore.checkSubscription(), fetchUser()]);
      if (!subscriptionStore.isSubscribed) {
        router.push('/(main)/subscribe');
        return;
      }

      const mode = deriveModeFromApiWorkout(apiWorkout);
      workoutSessionStore.setMode(mode);
      workoutSessionStore.setTemplateType(tt);

      if (mode === 'guided') {
        guidedWorkoutPlayerStore.loadExistingWorkout(apiWorkout);
      } else {
        workoutPlayerStore.loadExistingWorkout(apiWorkout);
      }
      router.push('/(workout)/pre-workout');
    } catch (error) {
      console.warn('[handleResume] Error resuming workout', error);
    } finally {
      setLoadingTemplateType(null);
    }
  };

  const handleRestart = async () => {
    const tt = resumeMenuTemplateType;
    setResumeMenuTemplateType(null);
    if (!tt) return;
    await workoutSessionStore.skipAndClear(tt);
    handleCreateGuided(tt);
  };

  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      <FocusGuide autoFocus style={styles.topBarGuide}>
        <View style={styles.topBar}>
          <TopbarLogo />
          <Pressable
            style={({ focused }) => [
              styles.settingsButton,
              focused && styles.settingsButtonFocused,
            ]}
            onPress={() => router.push('/(main)/settings')}
          >
            <Ionicons name="settings-outline" size={28 * scale} color="#fff" />
          </Pressable>
        </View>
      </FocusGuide>

      {/* Main content — vertical scroll for multiple rows */}
      <ScrollView
        style={styles.mainScroll}
        contentContainerStyle={styles.mainScrollContent}
        showsVerticalScrollIndicator={false}
      >
        {!!error && (
          <ThemedText style={[styles.errorText, styles.horizontalInset]}>
            {error}
          </ThemedText>
        )}

        {visibleGuidedTypes.length > 0 && (
          <WorkoutCarousel
            title="Guided Workouts"
            templateTypes={visibleGuidedTypes}
            disabled={isLoading}
            loadingTemplateType={loadingTemplateType}
            onPress={handleCardPress}
            onCardFocus={handleCardFocus}
            pausedTemplateTypes={pausedTemplateTypes}
            scale={scale}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            focusTemplateType={effectiveFocusTemplateType}
            focusNonce={focusNonce}
          />
        )}

        {visibleYogaTypes.length > 0 && (
          <WorkoutCarousel
            title="Yoga"
            templateTypes={visibleYogaTypes}
            disabled={isLoading}
            loadingTemplateType={loadingTemplateType}
            onPress={handleCardPress}
            onCardFocus={handleCardFocus}
            pausedTemplateTypes={pausedTemplateTypes}
            scale={scale}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            focusTemplateType={effectiveFocusTemplateType}
            focusNonce={focusNonce}
          />
        )}

        <View style={styles.bottomHintContainer}>
          {modificationNames.length > 0 && (
            <ThemedText
              style={[
                styles.bottomHint,
                styles.modificationHint,
                styles.horizontalInset,
              ]}
            >
              {'Some workouts may be hidden because of your '}
              {renderNameList(modificationNames, styles.modificationHintName)}
              {` modification${modificationNames.length > 1 ? 's' : ''}.`}
            </ThemedText>
          )}

          <ThemedText style={[styles.bottomHint, styles.horizontalInset]}>
            {'HiNote Life - Powered by fit52'}
          </ThemedText>
        </View>

        {!isLoading && ENABLE_CARD_WORKOUT && (
          <View style={[styles.cardWorkoutRow, styles.horizontalInset]}>
            <Pressable
              style={({ focused }) => [
                styles.cardWorkoutButton,
                focused && styles.cardWorkoutButtonFocused,
              ]}
              onPress={() => {
                if (workoutSessionStore.hasPausedWorkout('MINI')) {
                  setResumeMenuTemplateType('MINI');
                } else {
                  handleCreateCard();
                }
              }}
            >
              <ThemedText style={styles.cardWorkoutButtonText}>
                {workoutSessionStore.hasPausedWorkout('MINI')
                  ? 'Resume Card Workout'
                  : 'Create Card Workout'}
              </ThemedText>
            </Pressable>
          </View>
        )}
      </ScrollView>

      <ResumeActionsMenu
        visible={resumeMenuTemplateType != null}
        title={
          resumeMenuTemplateType
            ? getWorkoutTranslation(resumeMenuTemplateType).title
            : ''
        }
        onResume={handleResume}
        onRestart={handleRestart}
        onCancel={() => setResumeMenuTemplateType(null)}
      />
    </ThemedView>
  );
}

export default observer(HomeScreen);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      flex: 1,
    },
    topBarGuide: {
      width: '100%',
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 24 * scale,
      paddingHorizontal: 24 * scale,
    },
    settingsButton: {
      padding: 10 * scale,
      borderRadius: 8 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      right: 14 * scale,
      position: 'absolute',
    },
    settingsButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
    mainScroll: {
      flex: 1,
    },
    mainScrollContent: {
      paddingBottom: 40 * scale,
      gap: 16 * scale,
    },
    horizontalInset: {
      paddingHorizontal: HORIZONTAL_INSET * scale,
    },
    errorText: {
      fontSize: 14 * scale,
      color: '#ff6b6b',
    },
    bottomHint: {
      fontSize: 14 * scale,
      color: 'rgba(255,255,255,0.8)',
      textAlign: 'center',
    },
    modificationHint: {
      marginBottom: 12 * scale,
    },
    bottomHintContainer: {
      marginTop: 6 * scale,
    },
    // Bold span nested inside modificationHint; inherits size/color, adds weight.
    modificationHintName: {
      fontWeight: '700',
    },
    cardWorkoutRow: {
      flexDirection: 'row',
      marginTop: 8 * scale,
    },
    cardWorkoutButton: {
      paddingVertical: 14 * scale,
      paddingHorizontal: 32 * scale,
      backgroundColor: colors.brand.primary,
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    cardWorkoutButtonFocused: {
      borderColor: '#fff',
      backgroundColor: colors.brand.secondary,
    },
    cardWorkoutButtonText: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
  });
}
